import React, { createContext, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import { useIsLoggingIn } from '@order/AuthMachine';
import { LoadingAnimation } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useCart, useCustomerCredit, usePaymentMethods } from '@order/hooks';

import {
  getDefaultDropoffLocation,
  getDefaultPaymentMethod,
} from './Checkout.utils';
import {
  useAvailableWantedTimes,
  useCheckoutCredit,
  useCheckoutFieldsValidation,
  useCheckoutHandlers,
  useCheckoutLedger,
  useCheckoutPayload,
  useDeliveryEstimate,
  useFutureDateModal,
  useOrderAddress,
  useOrderValidation,
  useSubmitOrder,
} from './hooks';

// ─── Context ──────────────────────────────────────────────────────

const CheckoutContext = createContext<CheckoutCtx | undefined>(undefined);

export const CheckoutContextProvider = ({ children }: ViewProps) => {
  const customerData = useCustomer();
  const cartData = useCart();
  const wantedTimesData = useAvailableWantedTimes();
  const paymentMethodsData = usePaymentMethods();
  const creditData = useCustomerCredit({ requestPolicy: 'cache-and-network' });
  const deliveryEstimateData = useDeliveryEstimate(cartData.cart);

  // ─── Loading States ─────────────────────────────────────────────

  const isLoggingIn = useIsLoggingIn();
  const isLoadingCustomer = customerData.isFetchingCustomer;
  const isLoadingCart = cartData.isFetchingCart;
  const { isLoadingPaymentMethods } = paymentMethodsData;
  const { isLoadingCredits } = creditData;
  const isLoadingCheckout =
    isLoggingIn ||
    isLoadingCustomer ||
    isLoadingCart ||
    isLoadingPaymentMethods ||
    isLoadingCredits;

  // ─── Prevent State Initialization ───────────────────────────────

  if (isLoadingCheckout) {
    return <LoadingAnimation />;
  }

  // ─── Context State ──────────────────────────────────────────────

  return (
    <CheckoutContextState
      customerData={customerData}
      cartData={cartData}
      wantedTimesData={wantedTimesData}
      paymentMethodsData={paymentMethodsData}
      creditData={creditData}
      deliveryEstimateData={deliveryEstimateData}
    >
      {children}
    </CheckoutContextState>
  );
};

const CheckoutContextState = (props: CheckoutStateCtx & ViewProps) => {
  const {
    customerData,
    cartData,
    wantedTimesData,
    paymentMethodsData,
    creditData,
    deliveryEstimateData,
    children,
  } = props;

  // ─── Checkout Payload ────────────────────────────────────────────────

  const paymentMethod = getDefaultPaymentMethod(
    paymentMethodsData.paymentMethods,
  );

  const dropoffLocation = getDefaultDropoffLocation(
    cartData.cart,
    cartData.cartOrderType,
  );

  const payloadState = useCheckoutPayload({
    wantedTime: cartData?.cart?.availableWantedTimes?.[0]?.time,
    paymentMethodId: paymentMethod?.id,
    paymentMethodType: paymentMethod?.__typename,
    useCredit: creditData?.useCredit,
    dropoffLocationId: dropoffLocation?.id,
    contactNumber: customerData?.customer?.phoneNumber,
  });

  // ─── Query Derived Data ─────────────────────────────────────────

  const addressData = useOrderAddress(cartData.cart);

  // ─── Checkout State ─────────────────────────────────────────────

  const futureDateModalState = useFutureDateModal();
  const validationState = useCheckoutFieldsValidation(cartData.cartOrderType);
  const payloadHandlers = useCheckoutHandlers({
    setPayload: payloadState.setPayload,
    setShouldShowFutureDateModal:
      futureDateModalState.setShouldShowFutureDateModal,
  });

  // ─── State Derived Data ─────────────────────────────────────────

  const checkoutCreditData = useCheckoutCredit({
    ...cartData,
    ...creditData,
    ...payloadState,
  });

  const orderValidation = useOrderValidation({
    ...cartData,
    ...payloadState.payload,
  });

  const ledgerData = useCheckoutLedger({
    cart: cartData.cart,
    payload: payloadState.payload,
    credit: checkoutCreditData.checkoutCredit,
  });

  // ────────────────────────────────────────────────────────────────

  const submitOrderData = useSubmitOrder({
    cartData,
    payload: payloadState.payload,
    ledger: ledgerData.ledger,
  });

  // ─── Memoized Data ──────────────────────────────────────────────

  const value: CheckoutCtx = useMemo(
    () => ({
      ...customerData,
      ...cartData,
      ...wantedTimesData,
      ...paymentMethodsData,
      ...creditData,
      ...submitOrderData,
      ...deliveryEstimateData,
      ...addressData,
      ...payloadState,
      ...futureDateModalState,
      ...validationState,
      ...payloadHandlers,
      ...checkoutCreditData,
      ...orderValidation,
      ...ledgerData,
    }),
    [
      customerData,
      cartData,
      wantedTimesData,
      paymentMethodsData,
      creditData,
      submitOrderData,
      deliveryEstimateData,
      addressData,
      payloadState,
      futureDateModalState,
      validationState,
      payloadHandlers,
      checkoutCreditData,
      orderValidation,
      ledgerData,
    ],
  );

  // ─── Context Provider ───────────────────────────────────────────

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

// ─── Hooks ────────────────────────────────────────────────────────

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);

  if (context === undefined) {
    throw new Error(
      'useCheckoutContext must be used within a <CheckoutContextProvider>',
    );
  }

  return context;
};

// ─── Types ────────────────────────────────────────────────────────

type CheckoutStateCtx = Readonly<{
  customerData: ReturnType<typeof useCustomer>;
  cartData: ReturnType<typeof useCart>;
  wantedTimesData: ReturnType<typeof useAvailableWantedTimes>;
  paymentMethodsData: ReturnType<typeof usePaymentMethods>;
  creditData: ReturnType<typeof useCustomerCredit>;
  deliveryEstimateData: ReturnType<typeof useDeliveryEstimate>;
}>;

type CheckoutQueries = ReturnType<typeof useCustomer> &
  ReturnType<typeof useCart> &
  ReturnType<typeof useAvailableWantedTimes> &
  ReturnType<typeof usePaymentMethods> &
  ReturnType<typeof useCustomerCredit> &
  ReturnType<typeof useDeliveryEstimate>;

type CheckoutCtx = CheckoutQueries &
  ReturnType<typeof useCheckoutPayload> &
  ReturnType<typeof useFutureDateModal> &
  ReturnType<typeof useCheckoutFieldsValidation> &
  ReturnType<typeof useCheckoutHandlers> &
  ReturnType<typeof useOrderAddress> &
  ReturnType<typeof useSubmitOrder> &
  ReturnType<typeof useCheckoutCredit> &
  ReturnType<typeof useOrderValidation> &
  ReturnType<typeof useCheckoutLedger>;
